/**
 * Motor "construir" — montar peca, testar, refazer se falhar.
 *
 * D6/D7: tentativas_livres e sempre true. Falhar no teste NAO e derrota: o jogo
 * mostra o que ficou faltando e a crianca tenta de novo. Sem limite de
 * tentativas, sem som de erro, sem perda de progresso.
 *
 * A engenharia aqui e concreta: encaixar as pecas e depois apertar o martelo
 * para testar. Construir, testar, refazer.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

export class ConstruirEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.encaixes = [];
    this.pecas = [];
    this.grupo = null;
    this.passou = false;
    this.botaoTestar = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    if (payload.tentativas_livres !== true) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" tem tentativas_livres diferente de true. ` +
          'D6 e D7 nao permitem limite de tentativas neste jogo.'
      );
    }

    this.numeroDePecas = payload.pecas;
    this.nomeDoTeste = payload.teste;

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `${this.numeroDePecas} pecas, teste "${this.nomeDoTeste}".`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-construir';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);

    const largura = (this.numeroDePecas - 1) * 1.4;

    // Encaixes: os lugares vazios onde as pecas entram.
    for (let i = 0; i < this.numeroDePecas; i++) {
      const encaixe = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 0.12, 0.6),
        new THREE.MeshStandardMaterial({
          color: 0x5d5d5d,
          roughness: 1,
          transparent: true,
          opacity: 0.65,
          flatShading: true,
        })
      );
      encaixe.position.set(base.x - largura / 2 + i * 1.4, base.y + 0.08, base.z);
      encaixe.userData.ocupado = false;
      this.grupo.add(encaixe);
      this.encaixes.push(encaixe);
    }

    // Pecas soltas, na frente, para a crianca pegar.
    for (let i = 0; i < this.numeroDePecas; i++) {
      const peca = new THREE.Mesh(
        new THREE.BoxGeometry(0.4, 1.5, 0.4),
        new THREE.MeshStandardMaterial({ color: 0xb98a54, roughness: 1, flatShading: true })
      );
      peca.position.set(base.x - largura / 2 + i * 1.4, base.y + 0.85, base.z + 3.0);
      peca.userData.encaixadaEm = null;

      this.grupo.add(peca);
      this.pecas.push(peca);

      input.registrarArrastavel(peca, {
        destinos: () => this.encaixes,
        aoSoltar: (objeto, encaixe) => this.aoSoltarPeca(objeto, encaixe),
      });
    }

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.pecas[0], `peca da tarefa ${this.tarefa.id}`);
  }

  aoSoltarPeca(peca, encaixe) {
    const { audio } = this.ctx;

    // Libera o encaixe anterior, se a peca ja estava em algum.
    if (peca.userData.encaixadaEm) {
      peca.userData.encaixadaEm.userData.ocupado = false;
      peca.userData.encaixadaEm = null;
    }

    if (!encaixe || encaixe.userData.ocupado) {
      // D6 — solto no vazio ou em encaixe ja usado. Sem penalidade.
      audio.tocarTentarDeNovo();
      return;
    }

    encaixe.userData.ocupado = true;
    peca.userData.encaixadaEm = encaixe;
    peca.position.set(encaixe.position.x, encaixe.position.y + 0.75, encaixe.position.z);

    audio.tocarToque(this.pecasEncaixadas() - 1);

    if (this.pecasEncaixadas() === this.numeroDePecas) {
      this.mostrarBotaoTestar();
    }
  }

  pecasEncaixadas() {
    return this.pecas.filter((p) => p.userData.encaixadaEm).length;
  }

  /** A crianca decide quando esta pronta e aperta o martelo. */
  mostrarBotaoTestar() {
    if (this.botaoTestar) return;

    const botao = document.createElement('button');
    botao.className = 'botao-grande';
    botao.textContent = '\u{1F528}';
    botao.type = 'button';
    botao.setAttribute('aria-label', 'Testar a construcao');
    botao.style.right = '24px';
    botao.style.bottom = '160px';
    botao.addEventListener('click', () => this.testar());

    document.body.appendChild(botao);
    this.botaoTestar = botao;
  }

  testar() {
    const { audio, instrucao } = this.ctx;
    this.tentativas += 1;

    if (this.pecasEncaixadas() === this.numeroDePecas) {
      this.passou = true;
      audio.tocarSucesso();
      this.botaoTestar?.remove();
      this.botaoTestar = null;
      for (const peca of this.pecas) this.ctx.input.desregistrar(peca);
      return;
    }

    // D6 — nao passou no teste. Nao e derrota: e informacao.
    audio.tocarTentarDeNovo();
    const faltam = this.numeroDePecas - this.pecasEncaixadas();
    instrucao.mostrarTexto(`Quase! Ainda faltam ${faltam} peca(s) para encaixar.`);
  }

  concluiu() {
    return this.passou;
  }

  atualizar() {
    // As pecas ainda soltas balancam de leve, chamando o dedo (D4).
    for (const peca of this.pecas) {
      peca.rotation.z = peca.userData.encaixadaEm
        ? 0
        : Math.sin(performance.now() / 400 + peca.position.x) * 0.08;
    }
  }

  async concluir() {
    return super.concluir({ concluiu: this.passou });
  }

  descartar() {
    const { input, sceneManager } = this.ctx;

    for (const peca of this.pecas) input.desregistrar(peca);
    this.botaoTestar?.remove();
    this.botaoTestar = null;

    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => {
        o.geometry?.dispose();
        o.material?.dispose();
      });
    }

    this.encaixes = [];
    this.pecas = [];
    this.grupo = null;
    this.passou = false;
    super.descartar();
  }
}
